import React from "react";
import { safeUrl } from "../utils/security";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { fileUrl } from "../lib/api";
import RichTextRenderer from "./RichTextRenderer";

const TAGS = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  p: "p",
};

const tagClass = {
  h1: "font-heading text-5xl font-black leading-tight",
  h2: "font-heading text-3xl font-black",
  h3: "font-heading text-3xl font-black",
  h4: "font-heading text-2xl font-black",
  h5: "font-heading text-xl font-bold",
  h6: "font-heading text-lg font-bold uppercase tracking-wide",
  p: "text-gray-700",
};

function TextBlock({ as = "p", children, className = "" }) {
  if (!children) return null;
  const safeTag = TAGS[as] || "p";
  const Component = safeTag;
  return <RichTextRenderer as={Component} html={children} className={`${tagClass[safeTag]} ${className}`} />;
}

function SocialIcon({ href, label, children }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="text-brand-green hover:text-brand-orange transition">
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        {children}
      </svg>
    </a>
  );
}

export default function FounderBlock() {
  const { settings } = useSettings();

  if (settings.founder_enabled === false) return null;

  const imageWidth = settings.founder_image_width || "w-80";
  const ctaUrl = safeUrl(settings.founder_cta_url, "/catalog");
  const isInternal = ctaUrl.startsWith("/");

  return (
    <div className="flow-root">
      <div className="mx-auto mb-8 flex justify-center md:float-left md:mr-12 md:mb-8 md:justify-start md:mx-0">
        {settings.founder_image ? (
          <img
            src={fileUrl(settings.founder_image)}
            alt={settings.founder_name || "Rudy Lemoine"}
            className={`rounded-2xl shadow-lg ${imageWidth} max-w-full object-cover`}
          />
        ) : (
          <div className={`rounded-2xl shadow-lg ${imageWidth} max-w-full aspect-square bg-gray-100 flex items-center justify-center text-center p-6`}>
            <span className="text-gray-400 text-sm">Aucune photo de présentation définie</span>
          </div>
        )}
      </div>

      <div className="min-w-0">
        <TextBlock as={settings.founder_title_tag} className="mb-1">
          {settings.founder_title}
        </TextBlock>

        <TextBlock as={settings.founder_name_tag} className="text-brand-green mb-3">
          {settings.founder_name}
        </TextBlock>

        <TextBlock as={settings.founder_punchline_tag} className="text-gray-500 mb-4">
          {settings.founder_punchline}
        </TextBlock>

        <TextBlock as={settings.founder_body_1_tag} className="mb-4">
          {settings.founder_body_1}
        </TextBlock>

        <TextBlock as={settings.founder_body_2_tag} className="mb-6">
          {settings.founder_body_2}
        </TextBlock>

        <div className="flex gap-4 text-xl mb-6">
          <SocialIcon href={safeUrl(settings.founder_facebook_url)} label="Facebook SF Learning">
            <path d="M22 12a10 10 0 10-11.5 9.87v-6.99H7.9V12h2.6V9.8c0-2.57 1.53-4 3.88-4 1.12 0 2.29.2 2.29.2v2.52h-1.29c-1.27 0-1.66.79-1.66 1.6V12h2.83l-.45 2.88h-2.38v6.99A10 10 0 0022 12z" />
          </SocialIcon>
          <SocialIcon href={safeUrl(settings.founder_instagram_url)} label="Instagram SF Learning">
            <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm5 5a5 5 0 110 10 5 5 0 010-10zm6.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0zM12 9a3 3 0 100 6 3 3 0 000-6z" />
          </SocialIcon>
          <SocialIcon href={safeUrl(settings.founder_youtube_url)} label="YouTube SF Learning">
            <path d="M21.8 8s-.2-1.4-.8-2c-.7-.8-1.5-.8-1.9-.9C16.7 5 12 5 12 5s-4.7 0-7.1.1c-.4.1-1.2.1-1.9.9C2.4 6.6 2.2 8 2.2 8S2 9.6 2 11.2v1.6c0 1.6.2 3.2.2 3.2s.2 1.4.8 2c.7.8 1.6.8 2 .9 1.5.1 6.3.1 6.3.1s4.7 0 7.1-.1c.4-.1 1.2-.1 1.9-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.6c0-1.6-.2-3.2-.2-3.2zM9.8 14.5v-5l5 2.5-5 2.5z" />
          </SocialIcon>
          <SocialIcon href={safeUrl(settings.founder_linkedin_url)} label="LinkedIn SF Learning">
            <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.05-1.86-3.05-1.86 0-2.15 1.45-2.15 2.96v5.66H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.86 3.37-1.86 3.6 0 4.27 2.37 4.27 5.46v6.29zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
          </SocialIcon>
          <SocialIcon href={safeUrl(settings.founder_tiktok_url)} label="TikTok SF Learning">
            <path d="M12.75 2h2.25a4.5 4.5 0 004.5 4.5v2.25a6.75 6.75 0 01-4.5-1.68v6.93a5.25 5.25 0 11-5.25-5.25c.17 0 .33.01.5.03v2.27a3 3 0 103 3V2z" />
          </SocialIcon>
        </div>

        {settings.founder_cta_text && (isInternal ? (
          <Link to={ctaUrl} className="btn-primary inline-flex">
            {settings.founder_cta_text}
          </Link>
        ) : (
          <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex">
            {settings.founder_cta_text}
          </a>
        ))}
      </div>
    </div>
  );
}
